'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { styled } from '@mui/material/styles';
import Checkbox, { checkboxClasses } from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { useStore } from '@base-ui/utils/store';
import { useStableCallback } from '@base-ui/utils/useStableCallback';
import { useEventCalendarStoreContext } from '@mui/x-scheduler-headless/use-event-calendar-store-context';
import { schedulerResourceSelectors } from '@mui/x-scheduler-headless/scheduler-selectors';
import clsx from 'clsx';
import { getPaletteVariants } from "../../internals/utils/tokens.mjs";
import { useEventCalendarStyledContext } from "../EventCalendarStyledContext.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ResourcesLegendRoot = styled('section', {
  name: 'MuiEventCalendar',
  slot: 'ResourcesLegend'
})(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1),
  overflowY: 'auto',
  scrollbarWidth: 'thin'
}));
const ResourcesLegendLabel = styled(Typography, {
  name: 'MuiEventCalendar',
  slot: 'ResourcesLegendLabel'
})(({
  theme
}) => _extends({}, theme.typography.subtitle2, {
  fontWeight: theme.typography.fontWeightMedium,
  paddingLeft: theme.spacing(1.25),
  paddingBottom: theme.spacing(1)
}));
const ResourcesLegendItemRoot = styled(FormControlLabel, {
  name: 'MuiEventCalendar',
  slot: 'ResourcesLegendItem'
})(({
  theme
}) => ({
  marginLeft: 0,
  marginRight: 0,
  paddingLeft: `calc(var(--resource-depth) * ${theme.spacing(2)})`,
  [`& .${checkboxClasses.root}`]: {
    color: 'var(--event-main)',
    [`&.${checkboxClasses.checked}`]: {
      color: 'var(--event-main)'
    }
  },
  variants: getPaletteVariants(theme)
}));
const ResourcesLegendItemName = styled(Typography, {
  name: 'MuiEventCalendar',
  slot: 'ResourcesLegendItemName'
})(({
  theme
}) => ({
  fontSize: theme.typography.body2.fontSize
}));
function ResourcesLegendItem(props) {
  const {
    resource,
    isVisible,
    onToggle
  } = props;
  const {
    classes,
    localeText
  } = useEventCalendarStyledContext();
  const store = useEventCalendarStoreContext();
  const eventColor = useStore(store, schedulerResourceSelectors.defaultEventColor, resource.id);
  const depth = useStore(store, schedulerResourceSelectors.resourceDepth, resource.id);
  return /*#__PURE__*/_jsx(ResourcesLegendItemRoot, {
    className: classes.resourcesLegendItem,
    style: {
      '--resource-depth': Math.min(depth, 2)
    },
    "data-palette": eventColor,
    control: /*#__PURE__*/_jsx(Checkbox, {
      className: classes.resourcesLegendItemCheckbox,
      checked: isVisible,
      onChange: event => onToggle(resource.id, event),
      size: "small",
      slotProps: {
        input: {
          'aria-label': isVisible ? localeText.hideEventsLabel(resource.title) : localeText.showEventsLabel(resource.title)
        }
      }
    }),
    label: /*#__PURE__*/_jsx(ResourcesLegendItemName, {
      className: classes.resourcesLegendItemName,
      children: resource.title
    })
  });
}
export const ResourcesLegend = /*#__PURE__*/React.forwardRef(function ResourcesLegend(props, forwardedRef) {
  const {
    classes,
    localeText
  } = useEventCalendarStyledContext();
  const store = useEventCalendarStoreContext();
  const resources = useStore(store, schedulerResourceSelectors.processedResourceFlatList);
  const visibleResources = useStore(store, schedulerResourceSelectors.visibleMap);
  const parentIdLookup = useStore(store, schedulerResourceSelectors.resourceParentIdLookup);
  const handleToggle = useStableCallback((resourceId, event) => {
    const checked = event.target.checked;
    const raw = store.state.visibleResources;
    const newVisibleResources = _extends({}, raw, {
      [resourceId]: checked
    });

    // Also enable hidden ancestors so the child becomes effectively visible.
    if (checked) {
      let currentId = parentIdLookup.get(resourceId) ?? null;
      while (currentId != null) {
        if (raw[currentId] === false) {
          newVisibleResources[currentId] = true;
        }
        currentId = parentIdLookup.get(currentId) ?? null;
      }
    }
    store.setVisibleResources(newVisibleResources, event.nativeEvent);
  });
  if (resources.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsxs(ResourcesLegendRoot, _extends({
    ref: forwardedRef,
    "aria-label": localeText.resourcesLegendSectionLabel
  }, props, {
    className: clsx(props.className, classes.resourcesLegend),
    children: [/*#__PURE__*/_jsx(ResourcesLegendLabel, {
      className: classes.resourcesLegendLabel,
      children: localeText.resourcesLabel
    }), resources.map(resource => /*#__PURE__*/_jsx(ResourcesLegendItem, {
      resource: resource,
      isVisible: visibleResources[resource.id] !== false,
      onToggle: handleToggle
    }, resource.id))]
  }));
});
if (process.env.NODE_ENV !== "production") ResourcesLegend.displayName = "ResourcesLegend";